import { FormGroup } from "@material-ui/core";
import { Typography } from "@mui/material";
import { format, getMonth, getYear } from "date-fns";
import React, { useEffect, useRef, RefObject } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { StyledIcon } from "../StyledIcon/StyledIcon";
import { Box, TextFielddBox } from "./styles";
import { monthsBR, ptbr } from "../../shared/calendar";

interface CalendarRangeProps {
  name: string;
  label: string;
  value?: [Date, Date] | [null, null];
  onChangeDateStartEnd?: (dateBegin: Date) => void;
  onBlur?: (e: React.FocusEvent<any>) => void;
  clearValueCalendar?: boolean;
  disabledTextField?: boolean;
  hasError?: boolean;
  messageError?: string;
  disablePastDates?: boolean;
}

const CalendarSingle: React.FC<CalendarRangeProps> = ({ ...props }) => {
  const [singleDate, setSingleDate] = React.useState<Date | null>(
    props.value?.[0] ?? null
  );
  const [isOpen, setIsOpen] = React.useState(false);
  const datePickerRef: RefObject<HTMLDivElement> = useRef(null);

  const handleChange = (date: [Date, Date]) => {
    if (props.onChangeDateStartEnd) props.onChangeDateStartEnd(date[0]);
    setSingleDate(date[0]);
    setIsOpen(false);
  };

  const handleClick = (e: any) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  function range(start: number, end: number) {
    return Array.from({ length: end - start }, (v, k) => k + start);
  }

  const years = range(2023, new Date().getFullYear() + 1);

  const getDate = (startDate: Date | null) =>
    startDate ? format(startDate, "dd/MM/yyyy") : "";

  useEffect(() => {
    if (props.value && props.value[0] === null) {
      setSingleDate(null);
    }
  }, [props.value?.[0]]);

  useEffect(() => {
    if (props.clearValueCalendar) {
      setSingleDate(null);
    }
  }, [props.clearValueCalendar]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [datePickerRef]);

  return (
    <FormGroup ref={datePickerRef}>
      <Typography
        sx={{
          color: "#58595B",
          fontFamily: "Open Sans",
          fontSize: "14px",
          fontWeight: "700",
          paddingBottom: "4px",
        }}
        noWrap
      >
        {props.label}
      </Typography>
      <TextFielddBox
        disabled={props.disabledTextField}
        type="text"
        placeholder="DD/MM/AAAA"
        id="outlined-basic"
        variant="outlined"
        name={props.name}
        onClick={handleClick}
        onBlur={props.onBlur}
        value={
          props.value?.[0]
            ? format(props.value[0], "dd/MM/yyyy")
            : getDate(singleDate)
        }
        autoComplete="off"
        InputProps={{
          readOnly: true,
        }}
        fullWidth
        error={props.hasError}
        helperText={props.messageError}
      />

      <Box>
        {isOpen && !props.disabledTextField && (
          <DatePicker
            renderCustomHeader={({
              date,
              changeYear,
              changeMonth,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }) => (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  backgroundColor: "#fff",
                }}
              >
                <div>
                  <button
                    type="button"
                    style={{
                      padding: "0",
                      cursor: "pointer",
                      background: "#FFF",
                      border: "none",
                    }}
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                  >
                    <StyledIcon
                      iconType="left-arrow"
                      sx={{
                        height: "12px",
                        width: "14px",
                      }}
                    />
                  </button>

                  <select
                    style={{
                      color: "#58595B",
                      fontFamily: "Open Sans",
                      fontSize: "14px",
                      border: "none",
                      textAlign: "center",
                      padding: "0 4px 0 4px",
                      cursor: "pointer",
                    }}
                    value={monthsBR[getMonth(date)]}
                    onChange={({ target: { value } }) =>
                      changeMonth(monthsBR.indexOf(value))
                    }
                  >
                    {monthsBR.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <select
                    style={{
                      backgroundColor: "#fff",
                      color: "#58595B",
                      fontSize: "14px",
                      border: "none",
                      cursor: "pointer",
                      marginRight: "25px",
                    }}
                    value={getYear(date)}
                    onChange={({ target: { value } }) =>
                      changeYear(parseInt(value))
                    }
                  >
                    {years.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    style={{
                      border: "none",
                      padding: "0",
                      cursor: "pointer",
                      background: "#FFF",
                    }}
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                  >
                    <StyledIcon
                      iconType="right-arrow"
                      sx={{
                        height: "12px",
                        width: "14px",
                      }}
                    />
                  </button>
                </div>
              </div>
            )}
            wrapperClassName="datePicker"
            selected={singleDate}
            locale={ptbr}
            onChange={handleChange}
            selectsRange
            inline
            isClearable
            minDate={props.disablePastDates ? new Date() : undefined}
          />
        )}
      </Box>
    </FormGroup>
  );
};

export default CalendarSingle;
